/*
 * SCSS → Tailwind token mapping (BuilderOptionsBlockSingleSelectFollowUp.scss deleted):
 *
 * Shared bob-* classes (previously relied on BuilderOptionsBlock.scss being injected):
 *   .bob-options-section → flex flex-col gap-[10px]
 *   .bob-options-list    → flex flex-col gap-[8px]
 *
 * Own sfb-* classes:
 *   .sfb-option-container:
 *     display:flex / flex-direction:column / gap:10px → flex flex-col gap-[10px]
 *
 *   .sfb-option-row:
 *     display:flex / align-items:center / gap:12px → flex items-center gap-[12px]
 *     > :first-child { flex:1 } → [&>:first-child]:flex-1
 *
 *   .sfb-switch-label:
 *     font-size:13px / color:--text-medium / white-space:nowrap
 *     → text-[13px] text-text-light whitespace-nowrap
 *     (13px has no exact DS token; arbitrary text-[13px] used)
 *
 *   .sfb-followup-row:
 *     display:flex / flex-direction:column / gap:8px / padding-left:16px
 *     → flex flex-col gap-[8px] pl-sp-2
 *     (pl-sp-2 = 16px)
 *
 *   .sfb-followup-required:
 *     display:flex / align-items:center / gap:8px → flex items-center gap-[8px]
 *
 * Arbitrary values: gap-[10px], gap-[8px], gap-[12px], text-[13px]
 * (no exact DS tokens at those sizes).
 */
import { memo } from 'react'
import type { ChangeEvent } from 'react'
import { Input, Label, Switch } from '@quickmd-llc/design-system'
import { errorCodes, getUserFriendlyErrorMessage } from '../../../../../utils/errormessages'

type ConditionOption = {
  optionId: string
  optionValue: string
  isOptionError: boolean
  hasFollowUp: boolean
  followUpText: string
  followUpRequired: boolean
}

type Props = {
  options: ConditionOption[]
  builderId: string
  isRequired: boolean
  onChangeQuestionOptionValue: (builderId: string, optionId: string, value: string) => void
  onChangeQuestionOptionErrorValue: (builderId: string, optionId: string, errorText: string) => void
  onToggleFollowUp: (builderId: string, optionId: string) => void
  onChangeFollowUpText: (builderId: string, optionId: string, value: string) => void
  onChangeFollowUpRequired: (builderId: string, optionId: string, value: boolean) => void
}

const BuilderOptionsBlockSingleSelectFollowUp = memo(
  ({
    options,
    builderId,
    isRequired,
    onChangeQuestionOptionValue,
    onChangeQuestionOptionErrorValue,
    onToggleFollowUp,
    onChangeFollowUpText,
    onChangeFollowUpRequired,
  }: Props) => {
    const onChangeOptionValueHandler = (optionId: string, e: ChangeEvent<HTMLInputElement>) => {
      onChangeQuestionOptionValue(builderId, optionId, e.target.value)
    }

    const onUserBlur = (optionId: string) => {
      onChangeQuestionOptionErrorValue(builderId, optionId, getUserFriendlyErrorMessage(errorCodes.DUPLICATE_OPTIONS))
    }

    const handleSwitchChange = (option: ConditionOption, value: boolean) => {
      if (!value) {
        onChangeFollowUpText(builderId, option.optionId, '')
        onChangeFollowUpRequired(builderId, option.optionId, false)
      }
      onToggleFollowUp(builderId, option.optionId)
    }

    return (
      <div className='flex flex-col gap-[10px]'>
        <Label size='md'>Options</Label>

        <div className='flex flex-col gap-[8px]'>
          {options.map((option) => (
            <div key={option.optionId} className='flex flex-col gap-[10px]'>
              <div className='flex items-center gap-[12px] [&>:first-child]:flex-1'>
                <Input
                  value={option.optionValue}
                  onChange={(e) => onChangeOptionValueHandler(option.optionId, e)}
                  onBlur={() => onUserBlur(option.optionId)}
                  error={option.isOptionError}
                  placeholder='Option'
                />
                <Switch
                  size='s'
                  checked={!!option.hasFollowUp}
                  onChange={(value) => handleSwitchChange(option, value)}
                />
                <span className='whitespace-nowrap text-[13px] text-text-light'>Follow-up if selected</span>
              </div>

              {/* Follow-up prompt TEXT is intentionally omitted: the backend condition-option
                  Input contract only has IsRequired (no text field), so a custom prompt can't be
                  persisted. Only the backend-supported "required" flag is exposed here. */}
              {option.hasFollowUp && isRequired ?
                <div className='flex items-center gap-[8px] pl-sp-2'>
                  <Switch
                    size='s'
                    checked={!!option.followUpRequired}
                    onChange={(value) => onChangeFollowUpRequired(builderId, option.optionId, value)}
                  />
                  <Label size='sm'>Follow-up required</Label>
                </div>
              : null}
            </div>
          ))}
        </div>
      </div>
    )
  },
)

export { BuilderOptionsBlockSingleSelectFollowUp }
